import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import defaultState, { requiredFields } from '@models/agencyContact'

import PageContext from '@contexts/pageContext'

import useSelector from '@hooks/useSelector'

import * as agencyContactActions from '@redux/modules/agencyContact'
import type { AppDispatch } from '@redux/store'
import { DeleteParams } from '@redux/modules/module'

import type { AgencyContactModel, AgencyContactRequestOptions } from '@models/types'

type CreateAgencyContactParams = {
  agencyContactParams: Partial<AgencyContactModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyContactRequestOptions,
}

const createAgencyContact = (params: CreateAgencyContactParams) => {
  const { agencyContactParams, dispatch, requestOptions } = params
  const { createAgencyContact: createFn } = agencyContactActions

  return dispatch(createFn(agencyContactParams, requestOptions))
}

type DeleteAgencyContactParams = {
  agencyContact: DeleteParams<AgencyContactModel>,
  dispatch: AppDispatch,
}

const deleteAgencyContact = (params: DeleteAgencyContactParams) => {
  const { agencyContact, dispatch } = params
  const { deleteAgencyContact: deleteFn } = agencyContactActions

  return dispatch(deleteFn(agencyContact))
}

type UpdateAgencyContactParams = {
  agencyContactParams: Partial<AgencyContactModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyContactRequestOptions,
}

const updateAgencyContact = (params: UpdateAgencyContactParams) => {
  const { agencyContactParams, dispatch, requestOptions } = params
  const { updateAgencyContact: updateFn } = agencyContactActions

  // @ts-expect-error TS2345
  return dispatch(updateFn(agencyContactParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyContactForm(
  agencyContact: Partial<AgencyContactModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { validateOn } = options

  const agencyContactForm = useForm(
    defaultState,
    { entity: agencyContact, requiredFields, validateOn },
    [agencyContact.id, agencyContact.cache_key],
  )

  return {
    ...agencyContactForm,
  }
}

const useAgencyContact = (initEntity: Partial<AgencyContactModel> = {}) => {
  // @ts-expect-error TS2322
  const { entity: agencyContact }: { entity: AgencyContactModel } = useLatestEntity(initEntity, 'agencyContacts')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating,
    deleting,
    loading,
    updating,
  } = useSelector(reduxState => reduxState.agencyContacts)

  return {
    agencyContact,
    callbacks: {
      createAgencyContact: (
        agencyContactParams: Partial<AgencyContactModel>,
        requestOptions?: AgencyContactRequestOptions,
      ) => (
        createAgencyContact({ agencyContactParams, dispatch, requestOptions })
      ),
      createOrEditAgencyContact: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAgencyContactModal',
        payload: { agencyContact },
      }),
      deleteAgencyContact: () => deleteAgencyContact({ agencyContact, dispatch }),
      updateAgencyContact: (
        agencyContactParams: Partial<AgencyContactModel>,
        requestOptions?: AgencyContactRequestOptions,
      ) => (
        updateAgencyContact({ agencyContactParams, dispatch, requestOptions })
      ),
    },
    creating,
    deleting,
    loading,
    updating,
  }
}

export type UseAgencyContactPayload = ReturnType<typeof useAgencyContact>

export default useAgencyContact
